import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Gauge, Loader2, RefreshCw, ThumbsUp, BadgeCheck, HelpCircle, Zap, Timer, TrendingUp,
  ArrowUp, ArrowDown, Minus, X, Clock, ChevronRight, Database,
} from 'lucide-react';
import { toast } from 'sonner';

const TRUST_STYLE = {
  certified: 'bg-emerald-500',
  provisional: 'bg-amber-500',
  mixed: 'bg-sky-500',
  ad_hoc: 'bg-violet-500',
  unverified: 'bg-muted-foreground/40',
  unknown: 'bg-muted-foreground/20',
};
const TRUST_LABEL = {
  certified: 'Certified', provisional: 'Provisional', mixed: 'Mixed',
  ad_hoc: 'Ad-hoc', unverified: 'Unverified', unknown: 'Unknown',
};
const CACHE_STATUS = {
  valid: { label: 'Valid', cls: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300' },
  stale_governance: { label: 'Stale (governance changed)', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300' },
  expired: { label: 'Expired (TTL)', cls: 'bg-muted/40 border-border/40 text-muted-foreground' },
};

function Sparkline({ data = [], width = 60, height = 18 }) {
  const clean = (data || []).filter((v) => typeof v === 'number');
  if (clean.length < 2) return null;
  const max = Math.max(...clean);
  const min = Math.min(...clean);
  const range = max - min || 1;
  const pts = clean.map((v, i) => {
    const x = (i / (clean.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible opacity-80" data-testid="kpi-sparkline" aria-hidden>
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function fmtDelta(delta, unit) {
  const sign = delta > 0 ? '+' : '';
  if (unit === 'ms') return `${sign}${(delta / 1000).toFixed(1)}s`;
  if (unit === 'count') return `${sign}${delta}`;
  return `${sign}${delta} pts`;
}

function TrendArrow({ trend, goodDirection = 'up', unit = 'pts' }) {
  if (!trend) return null;
  const dir = trend.direction;
  const Icon = dir === 'up' ? ArrowUp : dir === 'down' ? ArrowDown : Minus;
  const isGood = goodDirection === 'neutral' || dir === 'flat' ? null : dir === goodDirection;
  const color = isGood === null ? 'text-muted-foreground' : isGood ? 'text-emerald-400' : 'text-rose-400';
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${color}`} data-testid="kpi-trend"
      title={`vs previous window: ${fmtDelta(trend.delta, unit)}`}>
      <Icon className="w-3 h-3" />{dir === 'flat' ? '0' : fmtDelta(trend.delta, unit)}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent = 'text-primary', hint, testid,
  trend, series, goodDirection = 'up', unit = 'pts', onClick }) {
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  return (
    <Wrapper type={clickable ? 'button' : undefined} onClick={onClick}
      className={`text-left rounded-xl border border-border/40 bg-card/40 p-4 flex flex-col gap-1 transition-colors ${clickable ? 'hover:border-primary/50 hover:bg-card/60 cursor-pointer' : ''}`}
      data-testid={testid} title={hint}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-muted-foreground text-[11px] uppercase tracking-wide">
          <Icon className={`w-3.5 h-3.5 ${accent}`} /> {label}
        </div>
        <div className={`flex items-center gap-2 ${accent}`}>
          {series && <Sparkline data={series} />}
        </div>
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="text-2xl font-heading font-semibold text-foreground leading-tight" data-testid={`${testid}-value`}>{value}</div>
        <div className="flex items-center gap-1">
          {trend && <TrendArrow trend={trend} goodDirection={goodDirection} unit={unit} />}
          {clickable && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
      </div>
      {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
    </Wrapper>
  );
}

export default function ExploreKpisPage() {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [days, setDays] = useState(30);
  const [kpis, setKpis] = useState(null);
  const [obs, setObs] = useState(null);
  const [cache, setCache] = useState(null);
  const [loading, setLoading] = useState(true);
  // Coverage drill-down
  const [showCoverage, setShowCoverage] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  useEffect(() => {
    api.getSemanticModels().then((r) => setModels(r.models || r || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setShowCoverage(false);
    try {
      const [k, o, c] = await Promise.all([
        api.getExploreKpis(modelId, days),
        api.getExploreObservability(modelId, days),
        api.getCacheInsights(modelId, days),
      ]);
      setKpis(k);
      setObs(o);
      setCache(c);
    } catch (e) {
      toast.error(e.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, [modelId, days]);

  useEffect(() => { load(); }, [load]);

  const openCoverage = async () => {
    setShowCoverage(true);
    setCoverageLoading(true);
    try {
      const r = await api.getCoverageUnverified(modelId, days);
      setCoverage(r);
    } catch (e) {
      toast.error(e.message || 'Failed to load coverage detail');
      setShowCoverage(false);
    } finally {
      setCoverageLoading(false);
    }
  };

  const acc = kpis?.acceptance || {};
  const cov = kpis?.certified_coverage || {};
  const promo = kpis?.promotion || {};
  const lat = kpis?.latency || {};
  const dist = cov.distribution || {};
  const trend = kpis?.trend || {};
  const spark = kpis?.sparklines || {};
  const distTotal = Object.values(dist).reduce((a, b) => a + b, 0) || 0;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="explore-kpis-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <Gauge className="w-5 h-5 text-emerald-400" /> AI Explore — Success KPIs
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Program health from privacy-safe telemetry — acceptance, certified coverage, ad-hoc promotion, clarification &amp; cache rates. No customer values.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="kpi-days-select">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
          <Button size="sm" variant="outline" onClick={load} className="gap-1 text-xs h-8" data-testid="kpi-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs text-muted-foreground">Model</Label>
        <select value={modelId} onChange={(e) => setModelId(e.target.value)}
          className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="kpi-model-select">
          <option value="">All models</option>
          {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
        </select>
        {kpis && (
          <span className="text-[11px] text-muted-foreground" data-testid="kpi-total-asks">
            {kpis.total_asks} asks in window · trend vs previous {days}d
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : !kpis ? (
        <div className="text-sm text-muted-foreground py-12 text-center" data-testid="kpi-empty">No KPI data yet.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="kpi-cards">
            <KpiCard icon={ThumbsUp} label="First-response acceptance" accent="text-emerald-400"
              value={`${acc.rate ?? 0}%`} sub={`${acc.up || 0} up · ${acc.down || 0} down · ${acc.rated || 0} rated`}
              hint="Thumbs-up as a share of all rated answers" testid="kpi-acceptance"
              trend={trend.acceptance_rate} series={spark.acceptance_rate} goodDirection="up" unit="pts" />
            <KpiCard icon={BadgeCheck} label="Certified-answer coverage" accent="text-cyan-400"
              value={`${cov.certified_pct ?? 0}%`} sub={`${cov.governed_pct ?? 0}% governed · ${cov.labeled_answers || 0} labeled · view gaps`}
              hint="Click to see which asked questions are still unverified" testid="kpi-coverage"
              trend={trend.certified_pct} series={spark.certified_pct} goodDirection="up" unit="pts"
              onClick={openCoverage} />
            <KpiCard icon={TrendingUp} label="Ad-hoc → certified" accent="text-violet-400"
              value={`${promo.certified || 0}/${promo.requests || 0}`} sub={`${promo.cert_rate ?? 0}% certified · ${promo.pending || 0} pending · ${promo.promoted_learning || 0} learned`}
              hint="Certification requests raised from ad-hoc answers and how many were certified" testid="kpi-promotion" />
            <KpiCard icon={HelpCircle} label="Clarification rate" accent="text-amber-400"
              value={`${kpis.clarification_rate ?? 0}%`} sub={`${kpis.clarifications || 0} clarifying prompts`}
              hint="Share of asks that needed a clarifying question (lower is better)" testid="kpi-clarification"
              trend={trend.clarification_rate} series={spark.clarification_rate} goodDirection="down" unit="pts" />
            <KpiCard icon={Zap} label="Cache-hit rate" accent="text-sky-400"
              value={`${kpis.cache_hit_rate ?? 0}%`} sub={`${kpis.cache_hits || 0} served from governance-aware cache`}
              hint="Answers served from the governance-aware cache" testid="kpi-cache"
              trend={trend.cache_hit_rate} series={spark.cache_hit_rate} goodDirection="up" unit="pts" />
            <KpiCard icon={Timer} label="Latency (p95)" accent="text-rose-400"
              value={lat.p95_ms ? `${(lat.p95_ms / 1000).toFixed(1)}s` : '—'} sub={`avg ${lat.avg_ms ? (lat.avg_ms / 1000).toFixed(1) + 's' : '—'}`}
              hint="95th-percentile answer latency (lower is better)" testid="kpi-latency"
              trend={trend.p95_ms} series={spark.p95_ms} goodDirection="down" unit="ms" />
          </div>

          {showCoverage && (
            <div className="rounded-xl border border-cyan-500/30 bg-card/40 p-4 space-y-3" data-testid="coverage-drilldown">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-cyan-400" /> Uncertified questions — what to certify next
                </h2>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setShowCoverage(false)} data-testid="coverage-close">
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
              {coverageLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : !coverage || coverage.items?.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4" data-testid="coverage-empty">
                  No uncertified questions in this window — every answered question resolved to a certified metric. 🎉
                </p>
              ) : (
                <>
                  <p className="text-[11px] text-muted-foreground">
                    {coverage.unverified_questions} distinct uncertified questions across {coverage.answered} answered asks. Certify the measures below in the Metric Registry to raise coverage.
                  </p>
                  <div className="space-y-1.5" data-testid="coverage-list">
                    {coverage.items.map((it, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-border/40 bg-background/40 px-3 py-2"
                        data-testid={`coverage-item-${i}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground truncate">{it.question}</p>
                          {it.measures?.length > 0 && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              measures: {it.measures.map((m) => String(m).replace(/_/g, ' ')).join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className={`text-[9px] ${TRUST_STYLE[it.trust_status] ? '' : ''} border-border/40 text-muted-foreground`}>
                            {TRUST_LABEL[it.trust_status] || it.trust_status}
                          </Badge>
                          <span className="text-[11px] text-foreground font-medium" title="times asked">{it.asks}×</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="rounded-xl border border-border/40 bg-card/30 p-4 space-y-3" data-testid="kpi-trust-dist">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground">Answer trust distribution</h2>
              <span className="text-[11px] text-muted-foreground">{cov.labeled_answers || 0} labeled answers</span>
            </div>
            {distTotal === 0 ? (
              <p className="text-xs text-muted-foreground">No trust-labeled answers in this window yet.</p>
            ) : (
              <>
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted/30">
                  {['certified', 'provisional', 'mixed', 'ad_hoc', 'unverified', 'unknown'].map((k) => (
                    dist[k] ? (
                      <div key={k} className={TRUST_STYLE[k]} style={{ width: `${(dist[k] / distTotal) * 100}%` }}
                        title={`${TRUST_LABEL[k]}: ${dist[k]}`} data-testid={`kpi-trust-bar-${k}`} />
                    ) : null
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(dist).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`kpi-trust-legend-${k}`}>
                      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${TRUST_STYLE[k] || TRUST_STYLE.unknown}`} />
                      {TRUST_LABEL[k] || k}
                      <span className="text-foreground font-medium">{v}</span>
                      <span>({Math.round((v / distTotal) * 100)}%)</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {cache && (
            <div className="rounded-xl border border-border/40 bg-card/30 p-4 space-y-3" data-testid="kpi-cache-insights">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Database className="w-4 h-4 text-sky-400" /> Cache insights
                </h2>
                <span className="text-[11px] text-muted-foreground" data-testid="cache-summary">
                  {cache.total_entries} cached · {cache.total_hits} hits in window
                </span>
              </div>
              {(!cache.entries || cache.entries.length === 0) ? (
                <p className="text-xs text-muted-foreground py-2" data-testid="cache-empty">
                  Nothing cached yet. Repeat identical questions get served from the governance-aware cache and appear here.
                </p>
              ) : (
                <div className="space-y-1.5" data-testid="cache-list">
                  {cache.entries.map((e, i) => {
                    const st = CACHE_STATUS[e.status] || CACHE_STATUS.valid;
                    return (
                      <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-border/40 bg-background/40 px-3 py-2"
                        data-testid={`cache-item-${i}`}>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground truncate">{e.question}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" />
                            {e.age_hours != null ? `cached ${e.age_hours}h ago` : 'cached'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[11px] text-foreground font-medium" title="times served from cache">{e.hits}×</span>
                          <Badge variant="outline" className={`text-[9px] ${st.cls}`} data-testid={`cache-status-${i}`}>{st.label}</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {obs && (
            <div className="rounded-xl border border-border/40 bg-card/30 p-4 space-y-2" data-testid="kpi-observability">
              <h2 className="text-sm font-medium text-foreground">Stage observability</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div><div className="text-muted-foreground">Total asks</div><div className="text-lg text-foreground font-semibold" data-testid="obs-total">{obs.total_asks}</div></div>
                <div><div className="text-muted-foreground">Avg latency</div><div className="text-lg text-foreground font-semibold" data-testid="obs-avg">{obs.avg_ms ? (obs.avg_ms / 1000).toFixed(1) + 's' : '—'}</div></div>
                <div><div className="text-muted-foreground">Retry rate</div><div className="text-lg text-foreground font-semibold" data-testid="obs-retry">{obs.retry_rate}%</div></div>
                <div><div className="text-muted-foreground">Refusal rate</div><div className="text-lg text-foreground font-semibold" data-testid="obs-refusal">{obs.refusal_rate}%</div></div>
              </div>
              <Badge variant="outline" className="text-[10px] mt-1 border-emerald-500/30 text-emerald-600 dark:text-emerald-300" data-testid="kpi-privacy-note">
                Privacy-safe · aggregates only, no customer values
              </Badge>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export { ExploreKpisPage };
